import { useCallback, useEffect, useRef, useState } from 'react'

export const DEFAULT_DESCRIPTION_PADDING = '16px 16px 16px 16px'
export const DEFAULT_DESCRIPTION_ALIGN = 'start'
export const DEFAULT_SPACE_BETWEEN = 0
export const DEFAULT_IMAGE_HEIGHT = 200
export const DEFAULT_IMAGE_WIDTH = 100
export const MIN_IMAGE_HEIGHT = 80
export const MAX_IMAGE_HEIGHT = 600
export const MIN_SPACE_BETWEEN = 0
export const MAX_SPACE_BETWEEN = 80

type StyleMap = Record<string, unknown> | null | undefined

export const readPx = (value: unknown, fallback: number) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = parseInt(value, 10)
    return Number.isNaN(parsed) ? fallback : parsed
  }

  return fallback
}

export const clampImageHeight = (height: number) =>
  Math.min(MAX_IMAGE_HEIGHT, Math.max(MIN_IMAGE_HEIGHT, Math.round(height)))

export const descriptionJustify = (align?: string) => {
  if (align === 'center') {
    return 'center'
  }

  if (align === 'end') {
    return 'flex-end'
  }

  return 'flex-start'
}

export const verticalPadding = (padding: string) => {
  const parts = padding
    .trim()
    .split(/\s+/)
    .map((part) => parseInt(part, 10) || 0)

  if (parts.length <= 1) {
    return (parts[0] ?? 0) * 2
  }

  if (parts.length === 2) {
    return parts[0] * 2
  }

  return parts[0] + (parts[2] ?? 0)
}

export const descriptionAreaMinHeight = (contentHeight: number, padding: string) => {
  if (contentHeight <= 0) {
    return undefined
  }

  return contentHeight + verticalPadding(padding)
}

const hasSavedValue = (value: unknown) => value !== undefined && value !== null && value !== ''

// 0 is the preferences "auto" size. Storing it as a pixel height hides the image.
export const readImageHeight = (value: unknown) => {
  if (!hasSavedValue(value)) {
    return undefined
  }

  const height = readPx(value, DEFAULT_IMAGE_HEIGHT)
  return height === 0 ? undefined : height
}

export const imageHeightToStore = (value: number) => (value === 0 ? null : value)

export const legacyDescriptionPadding = (isHorizontal: boolean) =>
  isHorizontal ? '0px 8px 0px 8px' : DEFAULT_DESCRIPTION_PADDING

export const getDollarHandleMetrics = (styles: StyleMap) => {
  const descriptionPadding = hasSavedValue(styles?.descriptionPadding) ? String(styles?.descriptionPadding) : undefined
  const descriptionAlign =
    styles?.descriptionAlign === 'start' || styles?.descriptionAlign === 'center' || styles?.descriptionAlign === 'end'
      ? styles.descriptionAlign
      : undefined
  const isHorizontal = styles?.layout === 'horizontal'

  return {
    spaceBetween: hasSavedValue(styles?.spaceBetween) ? readPx(styles?.spaceBetween, DEFAULT_SPACE_BETWEEN) : undefined,
    descriptionPadding,
    descriptionAlign,
    imageHeight: readImageHeight(styles?.imageHeight),
    imageWidth: readPx(styles?.imageWidth, DEFAULT_IMAGE_WIDTH),
    isHorizontal,
    // Existing handles omit these keys and keep their original layout.
    // Equal text height starts once padding or alignment has been saved.
    equalizeDescriptions: descriptionPadding !== undefined || descriptionAlign !== undefined,
    resolvedDescriptionPadding: descriptionPadding ?? legacyDescriptionPadding(isHorizontal),
  }
}

const replaceAskNumbers = (value: string, permutation: Map<number, number>) => {
  let next = value

  permutation.forEach((_to, from) => {
    next = next.replace(new RegExp(`DollarHandles\\.Ask${from}(?!\\d)`, 'gi'), `DollarHandles.Ask__${from}__`)
    next = next.replace(new RegExp(`"MergeFieldName":"Ask ${from}"`, 'g'), `"MergeFieldName":"Ask __${from}__"`)
  })

  permutation.forEach((to, from) => {
    next = next.split(`DollarHandles.Ask__${from}__`).join(`DollarHandles.Ask${to}`)
    next = next.split(`"MergeFieldName":"Ask __${from}__"`).join(`"MergeFieldName":"Ask ${to}"`)
  })

  return next
}

type AskTaggedHandle = {
  description?: string | null
  text?: {
    textValue?: string | null
    htmlText?: string | null
  } | null
}

/**
 * After a reorder, DollarHandles.Ask tags follow the handle they refer to.
 * Ask 1 on a handle that moves into the second slot becomes Ask 2, including tags
 * inside the other descriptions that pointed at the handle that moved.
 */
export function remapDollarHandleAskTags<T extends AskTaggedHandle>(
  handles: T[],
  fromIndex: number,
  toIndex: number,
): T[] {
  if (fromIndex === toIndex) {
    return handles
  }

  const order = handles.map((_, index) => index)
  const [moved] = order.splice(fromIndex, 1)
  order.splice(toIndex, 0, moved)

  const permutation = new Map<number, number>()
  order.forEach((oldIndex, newIndex) => {
    if (oldIndex !== newIndex) {
      permutation.set(oldIndex + 1, newIndex + 1)
    }
  })

  if (permutation.size === 0) {
    return handles
  }

  return handles.map((handle) => {
    const description =
      typeof handle.description === 'string' ? replaceAskNumbers(handle.description, permutation) : handle.description
    const text = handle.text
      ? {
          ...handle.text,
          textValue:
            typeof handle.text.textValue === 'string'
              ? replaceAskNumbers(handle.text.textValue, permutation)
              : handle.text.textValue,
          htmlText:
            typeof handle.text.htmlText === 'string'
              ? replaceAskNumbers(handle.text.htmlText, permutation)
              : handle.text.htmlText,
        }
      : handle.text

    if (description === handle.description && text === handle.text) {
      return handle
    }

    return { ...handle, description, text }
  })
}

export function reorderDollarHandlePositions<T extends StyleMap>(
  styles: T,
  from: number,
  to: number,
  count: number,
): T {
  if (!styles) {
    return styles
  }

  const next: Record<string, unknown> = { ...styles }
  const positions = Array.from({ length: count }, (_, index) => next[`objectPosition-${index}`])
  const [moved] = positions.splice(from, 1)
  positions.splice(to, 0, moved)

  positions.forEach((position, index) => {
    const key = `objectPosition-${index}`
    if (position === undefined) {
      delete next[key]
      return
    }

    next[key] = position
  })

  return next as T
}

/**
 * Tracks the tallest description content box so every handle can share that height.
 * Measure the inner content, not the stretched panel, or the height will never shrink.
 */
export function useEqualMinHeight(count: number, observeKey: string) {
  const elements = useRef<Array<HTMLElement | null>>([])
  const [minHeight, setMinHeight] = useState(0)

  const setRef = useCallback((index: number, node: HTMLElement | null) => {
    elements.current[index] = node
  }, [])

  useEffect(() => {
    const measure = () => {
      let max = 0
      for (let index = 0; index < count; index++) {
        const element = elements.current[index]
        if (!element) {
          continue
        }

        max = Math.max(max, element.scrollHeight)
      }

      setMinHeight((previous) => (previous === max ? previous : max))
    }

    measure()

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(measure)
    for (let index = 0; index < count; index++) {
      const element = elements.current[index]
      if (element) {
        observer.observe(element)
      }
    }

    return () => observer.disconnect()
  }, [count, observeKey])

  return { minHeight, setRef }
}
